<template>
  <b-card class="mt-2 task-comments-card">
    <div class="comments-header">
      <h5 class="comments-title">
        التعليقات
      </h5>
      <b-spinner
        v-if="loading"
        small
        class="loading-spinner"
      />
    </div>

    <!-- Add Comment Form -->
    <div class="comment-form-wrapper">
      <b-form
        class="comment-form"
        @submit.prevent="submitComment"
      >
        <div class="comment-input-wrapper">
          <b-form-textarea
            v-model="newComment"
            placeholder="أضف تعليقاً..."
            :rows="isMobileLandscape ? 2 : 3"
            :disabled="loading"
            maxlength="2000"
            class="comment-textarea"
          />
          <div class="textarea-footer">
            <small class="character-count">{{ newComment.length }}/2000</small>
          </div>
        </div>
        <b-button
          type="submit"
          variant="primary"
          size="sm"
          class="submit-comment-btn"
          :disabled="!newComment.trim() || loading"
        >
          <feather-icon
            icon="SendIcon"
            size="16"
            class="btn-icon"
          />
          <span class="btn-text">إرسال</span>
        </b-button>
      </b-form>
    </div>

    <!-- Comments List -->
    <div
      v-if="loading && comments.length === 0"
      class="loading-container"
    >
      <b-spinner variant="primary" />
      <p class="loading-text">
        جاري التحميل...
      </p>
    </div>

    <div
      v-else-if="comments.length === 0"
      class="empty-state"
    >
      <p class="empty-text">
        لا توجد تعليقات بعد
      </p>
      <small class="empty-hint">كن أول من يعلق على هذه المهمة</small>
    </div>

    <div
      v-else
      class="comments-list"
    >
      <comment-item
        v-for="comment in comments"
        :key="comment.id"
        :comment="comment"
        :task-id="taskId"
        :current-user-id="currentUserId"
        @reply="handleReply"
        @edit="handleEdit"
        @delete="handleDelete"
        @refresh="loadComments"
      />
    </div>
  </b-card>
</template>

<script>
import {
  BCard,
  BForm,
  BFormTextarea,
  BButton,
  BSpinner,
} from 'bootstrap-vue'
import { mapGetters, mapActions } from 'vuex'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'
import Swal from 'sweetalert2'
import CommentItem from './CommentItem.vue'

export default {
  name: 'TaskComments',
  components: {
    BCard,
    BForm,
    BFormTextarea,
    BButton,
    BSpinner,
    CommentItem,
    FeatherIcon,
  },
  props: {
    taskId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      newComment: '',
      replyingTo: null,
      editingComment: null,
      isMobileLandscape: false,
    }
  },
  computed: {
    ...mapGetters('taskComments', {
      comments: 'comments',
      loading: 'loading',
      error: 'error',
    }),
    currentUserId() {
      try {
        const userData = JSON.parse(localStorage.getItem('userData') || '{}')
        return userData.id || null
      } catch (e) {
        return null
      }
    },
  },
  watch: {
    taskId() {
      this.loadComments()
    },
  },
  mounted() {
    this.loadComments()
    this.checkOrientation()
    window.addEventListener('resize', this.checkOrientation)
    window.addEventListener('orientationchange', this.checkOrientation)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkOrientation)
    window.removeEventListener('orientationchange', this.checkOrientation)
  },
  methods: {
    ...mapActions('taskComments', [
      'fetchTaskComments',
      'createComment',
      'updateComment',
      'removeComment',
    ]),
    async loadComments() {
      try {
        await this.fetchTaskComments({ taskId: this.taskId })
      } catch (error) {
        console.error('Error loading comments:', error)
      }
    },
    async submitComment() {
      if (!this.newComment.trim()) return

      try {
        await this.createComment({
          taskId: this.taskId,
          comment: this.newComment.trim(),
          parentId: this.replyingTo,
        })
        this.newComment = ''
        this.replyingTo = null
        this.$toast.success('تم إضافة التعليق بنجاح')
      } catch (error) {
        this.$toast.error('فشل في إضافة التعليق')
      }
    },
    handleReply(commentId) {
      this.replyingTo = commentId
      // Scroll to comment form
      this.$nextTick(() => {
        const form = this.$el.querySelector('textarea')
        if (form) {
          form.focus()
          form.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      })
    },
    handleEdit(comment) {
      this.editingComment = comment
    },
    async handleDelete(commentId) {
      const result = await Swal.fire({
        icon: 'warning',
        title: 'هل أنت متأكد؟',
        text: 'سيتم حذف التعليق نهائياً',
        showCancelButton: true,
        confirmButtonText: 'نعم، احذف',
        cancelButtonText: 'إلغاء',
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
      })

      if (result.isConfirmed) {
        try {
          await this.removeComment(commentId)
          Swal.fire({
            icon: 'success',
            title: 'تم!',
            text: 'تم حذف التعليق بنجاح',
            confirmButtonColor: '#06284D',
            timer: 2000,
            showConfirmButton: false,
          })
        } catch (error) {
          Swal.fire({
            icon: 'error',
            title: 'خطأ',
            text: (error.response && error.response.data && error.response.data.message) || 'فشل في حذف التعليق',
            confirmButtonColor: '#06284D',
          })
        }
      }
    },
    checkOrientation() {
      this.$nextTick(() => {
        const isMobile = window.innerWidth <= 768
        const isLandscape = window.innerHeight < window.innerWidth
        this.isMobileLandscape = isMobile && isLandscape
      })
    },
  },
}
</script>

<style scoped>
.task-comments-card {
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
  padding: 1rem;
}

.comments-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #e9ecef;
}

.comments-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #212529;
}

.loading-spinner {
  flex-shrink: 0;
}

.comment-form-wrapper {
  background-color: #f5f5f5;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 1.5rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.comment-form {
  width: 100%;
}

.comment-input-wrapper {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.comment-textarea {
  font-size: 0.9375rem;
  line-height: 1.5;
  border: 1px solid #ced4da;
  border-radius: 6px;
  padding: 10px 12px;
  resize: vertical;
  background-color: #fff;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.comment-textarea:focus {
  border-color: #06284D;
  box-shadow: 0 0 0 2px rgba(6, 40, 77, 0.2);
  outline: 0;
}

.textarea-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.5rem;
}

.character-count {
  font-size: 0.8rem;
  color: #6c757d;
}

.submit-comment-btn {
  width: auto;
  padding: 8px 20px;
  font-size: 0.9rem;
  font-weight: 600;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 38px;
  background-color: #06284D;
  border: none;
  transition: background-color 0.2s;
}

.submit-comment-btn:hover:not(:disabled) {
  background-color: #003d3a;
}

.btn-icon {
  flex-shrink: 0;
}

.btn-text {
  white-space: nowrap;
}

.loading-container {
  text-align: center;
  padding: 2rem 1rem;
}

.loading-text {
  margin-top: 0.75rem;
  color: #6c757d;
  font-size: 0.9rem;
}

.empty-state {
  text-align: center;
  padding: 2rem 1rem;
}

.empty-text {
  color: #6c757d;
  font-size: 0.95rem;
  margin-bottom: 0.5rem;
}

.empty-hint {
  color: #adb5bd;
  font-size: 0.85rem;
  display: block;
}

.comments-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

/* Mobile Responsive Styles */
@media (max-width: 768px) {
  .task-comments-card {
    padding: 0.75rem;
    margin-top: 1rem;
  }

  .comments-header {
    margin-bottom: 0.75rem;
    padding-bottom: 0.5rem;
  }

  .comments-title {
    font-size: 1.1rem;
  }

  .comment-form-wrapper {
    padding: 0.75rem;
    margin-bottom: 1rem;
  }

  .comment-textarea {
    font-size: 16px; /* Prevents zoom on iOS */
    padding: 0.625rem;
  }

  .submit-comment-btn {
    width: 100%;
    padding: 0.625rem 1rem;
    font-size: 0.9rem;
    min-height: 44px; /* Touch-friendly */
    justify-content: center;
  }

  .loading-container {
    padding: 1.5rem 0.75rem;
  }

  .loading-text {
    font-size: 0.85rem;
  }

  .empty-state {
    padding: 1.5rem 0.75rem;
  }

  .empty-text {
    font-size: 0.9rem;
  }

  .empty-hint {
    font-size: 0.8rem;
  }

  .comments-list {
    gap: 0.25rem;
  }
}

@media (max-width: 576px) {
  .task-comments-card {
    padding: 0.625rem;
    margin-top: 0.75rem;
  }

  .comments-header {
    margin-bottom: 0.625rem;
    padding-bottom: 0.5rem;
  }

  .comments-title {
    font-size: 1rem;
  }

  .comment-form-wrapper {
    padding: 0.625rem;
  }

  .comment-textarea {
    padding: 0.5rem;
    font-size: 16px;
  }

  .submit-comment-btn {
    padding: 0.625rem;
    font-size: 0.875rem;
    min-height: 44px;
  }

  .loading-container {
    padding: 1.25rem 0.5rem;
  }

  .empty-state {
    padding: 1.25rem 0.5rem;
  }

  .empty-text {
    font-size: 0.85rem;
  }

  .empty-hint {
    font-size: 0.75rem;
  }
}

/* Very small screens */
@media (max-width: 360px) {
  .task-comments-card {
    padding: 0.5rem;
  }

  .comments-title {
    font-size: 0.95rem;
  }

  .comment-form-body {
    padding: 0.5rem !important;
  }

  .comment-textarea {
    padding: 0.5rem;
    font-size: 16px;
  }

  .submit-comment-btn {
    padding: 0.5rem;
    font-size: 0.85rem;
  }
}

/* Touch-friendly improvements */
@media (hover: none) and (pointer: coarse) {
  .submit-comment-btn {
    min-height: 44px;
    padding: 0.75rem 1rem;
  }

  .comment-textarea {
    padding: 0.75rem;
  }

  .comment-form-body {
    padding: 0.875rem !important;
  }
}

/* Landscape orientation on mobile */
@media (max-width: 768px) and (orientation: landscape) {
  .comment-textarea {
    min-height: 60px;
    max-height: 80px;
  }

  .task-comments-card {
    padding: 0.75rem;
  }
}
</style>
